/**
 * Calculates a "nice" maximum value for a chart axis that includes padding
 * and results in round tick values.
 * 
 * @param dataMax The maximum value in the data
 * @param paddingMultiplier Multiplier for padding (default 1.1 = 10% padding)
 * @returns A nice round number >= dataMax * paddingMultiplier
 */
export const getNiceDomainMax = (dataMax: number, paddingMultiplier = 1.1): number => {
    if (!dataMax || dataMax === 0) return 100;
    
    // Add padding
    const target = dataMax * paddingMultiplier;
    
    // Find magnitude (power of 10)
    // e.g. 185 -> 100, 1850 -> 1000
    const magnitude = Math.pow(10, Math.floor(Math.log10(target)));
    
    // Normalize target to 1-10 range
    // e.g. 185 -> 1.85
    const normalized = target / magnitude;
    
    // Round up to nearest nice number suitable for 5 intervals (tickCount=6)
    // We use numbers that are divisible by 5 or 4 nicely
    let niceNormalized;
    if (normalized <= 1) niceNormalized = 1;
    else if (normalized <= 1.2) niceNormalized = 1.2; // 0.24 steps (ok) or 0.2 steps
    else if (normalized <= 1.25) niceNormalized = 1.25; // 0.25 steps
    else if (normalized <= 1.5) niceNormalized = 1.5;  // 0.3 steps
    else if (normalized <= 2) niceNormalized = 2;       // 0.4 steps
    else if (normalized <= 2.5) niceNormalized = 2.5;   // 0.5 steps
    else if (normalized <= 3) niceNormalized = 3;       // 0.6 steps
    else if (normalized <= 4) niceNormalized = 4;       // 0.8 steps
    else if (normalized <= 5) niceNormalized = 5;       // 1.0 steps
    else if (normalized <= 6) niceNormalized = 6;       // 1.2 steps
    else if (normalized <= 8) niceNormalized = 8;       // 1.6 steps
    else niceNormalized = 10;                           // 2.0 steps
    
    return niceNormalized * magnitude;
};

/** Extra headroom on bar charts so value labels (above/below bars) stay inside the plot. */
const BAR_LABEL_DOMAIN_PAD = 1.22;

/** Data within this fraction of an axis edge triggers extra label headroom. */
const AXIS_EDGE_THRESHOLD = 0.98;

/** Extra axis range (as a fraction of span) when data hugs the top/bottom. */
const LABEL_HEADROOM_FRACTION = 0.05;

/** When percent data nears 100%, keep the axis at 100 (no overshoot for label headroom). */
const PERCENT_AXIS_MAX = 100;

/**
 * Minimum on-screen gap (px) reserved below the lowest negative bar so its below-bar value label
 * clears the x-axis category text: ~10px label offset + ~11px text height + buffer.
 */
const NEGATIVE_LABEL_MIN_GAP_PX = 32;

const applyLabelHeadroom = (
    yMin: number,
    yMax: number,
    minData: number,
    maxData: number,
    options?: { format?: 'percent' | 'currency' | 'number' | 'days'; capPercentMaxAt100?: boolean },
    plotHeightPx?: number,
): [number, number] => {
    let nextMin = yMin;
    let nextMax = yMax;
    const span = nextMax - nextMin;
    if (span <= 0) return [nextMin, nextMax];

    const headroom = span * LABEL_HEADROOM_FRACTION;

    if (maxData > 0 && maxData >= nextMax * AXIS_EDGE_THRESHOLD) {
        // Only hard-bounded rates (e.g. auto-submission) opt into capPercentMaxAt100.
        // Gross margin and similar percents can exceed 100% and need normal headroom.
        if (options?.format === 'percent' && options.capPercentMaxAt100 === true) {
            nextMax = Math.min(PERCENT_AXIS_MAX, Math.max(nextMax, maxData));
        } else {
            nextMax += headroom;
        }
    }

    if (minData < 0) {
        // Always pad below the lowest bar so below-bar value labels clear x-axis category text.
        nextMin = Math.min(nextMin, minData - headroom);
        if (minData <= nextMin * AXIS_EDGE_THRESHOLD) {
            nextMin -= headroom;
        }

        // Guarantee a constant pixel gap regardless of plot height. Extending the domain floor by
        // `d` data-units maps to `d / (newSpan) * plotHeightPx` px; solving that >= G for d yields
        // d = G * S0 / (plotHeightPx - G), where S0 is the span above the lowest bar.
        if (plotHeightPx && plotHeightPx > NEGATIVE_LABEL_MIN_GAP_PX) {
            const G = NEGATIVE_LABEL_MIN_GAP_PX;
            const s0 = nextMax - minData;
            const d = (G * s0) / (plotHeightPx - G);
            nextMin = Math.min(nextMin, minData - d);
        }
    }

    return [nextMin, nextMax];
};

/** Preferred tick count for reporting bar charts (Recharts used tickCount={6}). */
const BAR_CHART_PREFERRED_TICK_COUNT = 6;
/** Allow a few more/fewer intervals so the domain can stay tight while keeping nice steps. */
const BAR_CHART_MIN_INTERVALS = 4;
const BAR_CHART_MAX_INTERVALS = 7;

/** Smallest nice step >= roughStep (1 / 2 / 2.5 / 5 × 10^n). */
const getNiceStep = (roughStep: number): number => {
    if (!Number.isFinite(roughStep) || roughStep <= 0) return 1;
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
    const normalized = roughStep / magnitude;
    let niceNormalized: number;
    if (normalized <= 1) niceNormalized = 1;
    else if (normalized <= 2) niceNormalized = 2;
    else if (normalized <= 2.5) niceNormalized = 2.5;
    else if (normalized <= 5) niceNormalized = 5;
    else niceNormalized = 10;
    return niceNormalized * magnitude;
};

const getNextNiceStep = (step: number): number => getNiceStep(step * 1.01);

const cleanStepValue = (value: number, step: number): number => {
    const decimals = Math.max(0, Math.ceil(-Math.log10(step)) + 1);
    return Number(value.toFixed(Math.min(12, decimals)));
};

const isMultipleOfStep = (value: number, step: number): boolean => {
    if (step <= 0) return false;
    const q = value / step;
    return Math.abs(q - Math.round(q)) < 1e-8;
};

/**
 * Expand domain to the tightest nice-step range that covers [yMin, yMax].
 * Prefers ~6 ticks but may use 5–8 so the axis stays compact. Pair with
 * `getBarChartYTicks` (not a fixed tickCount) so spacing stays even + nice.
 */
export const snapDomainToEvenNiceTicks = (
    yMin: number,
    yMax: number,
): [number, number] => {
    if (!Number.isFinite(yMin) || !Number.isFinite(yMax) || yMax <= yMin) {
        return [yMin, yMax];
    }

    const preferredIntervals = BAR_CHART_PREFERRED_TICK_COUNT - 1;
    const span = yMax - yMin;
    let step = getNiceStep(span / BAR_CHART_MAX_INTERVALS);
    const stepCap = getNiceStep(span) * 10;

    let bestMin = yMin;
    let bestMax = yMax;
    let bestWidth = Number.POSITIVE_INFINITY;
    let bestTickScore = Number.POSITIVE_INFINITY;
    let found = false;

    const consider = (min: number, max: number, intervals: number) => {
        const width = max - min;
        const tickScore = Math.abs(intervals - preferredIntervals);
        if (
            !found
            || width < bestWidth - 1e-9
            || (Math.abs(width - bestWidth) < 1e-9 && tickScore < bestTickScore)
        ) {
            found = true;
            bestMin = min;
            bestMax = max;
            bestWidth = width;
            bestTickScore = tickScore;
        }
    };

    for (let i = 0; i < 20 && step <= stepCap; i++, step = getNextNiceStep(step)) {
        for (let intervals = BAR_CHART_MIN_INTERVALS; intervals <= BAR_CHART_MAX_INTERVALS; intervals++) {
            if (yMin >= 0) {
                const min = 0;
                const max = cleanStepValue(step * intervals, step);
                if (max >= yMax - 1e-9) consider(min, max, intervals);
            } else if (yMax <= 0) {
                const max = 0;
                const min = cleanStepValue(-step * intervals, step);
                if (min <= yMin + 1e-9) consider(min, max, intervals);
            } else {
                const min = cleanStepValue(Math.floor(yMin / step - 1e-12) * step, step);
                const max = cleanStepValue(min + step * intervals, step);
                if (max >= yMax - 1e-9) consider(min, max, intervals);
            }
        }
    }

    if (found) return [bestMin, bestMax];

    // Fallback: fixed preferred interval count with a large enough step.
    step = getNiceStep(span / preferredIntervals);
    for (let i = 0; i < 12; i++) {
        if (yMin >= 0) {
            const max = step * preferredIntervals;
            if (max >= yMax - 1e-9) return [0, cleanStepValue(max, step)];
        } else if (yMax <= 0) {
            const min = -step * preferredIntervals;
            if (min <= yMin + 1e-9) return [cleanStepValue(min, step), 0];
        } else {
            const min = Math.floor(yMin / step - 1e-12) * step;
            const max = min + step * preferredIntervals;
            if (max >= yMax - 1e-9) {
                return [cleanStepValue(min, step), cleanStepValue(max, step)];
            }
        }
        step = getNextNiceStep(step);
    }
    return [yMin, yMax];
};

/**
 * Evenly spaced nice tick values for a domain from `snapDomainToEvenNiceTicks` /
 * `getBarChartYDomain`. Use as Recharts `<YAxis ticks={...} />` instead of tickCount.
 */
export const getBarChartYTicks = (yMin: number, yMax: number): number[] => {
    if (!Number.isFinite(yMin) || !Number.isFinite(yMax) || yMax <= yMin) {
        return [yMin, yMax];
    }
    const span = yMax - yMin;
    for (let intervals = BAR_CHART_MIN_INTERVALS; intervals <= BAR_CHART_MAX_INTERVALS; intervals++) {
        const step = span / intervals;
        if (!Number.isFinite(step) || step <= 0) continue;
        if (!isMultipleOfStep(yMin, step)) continue;
        // Domain from snapDomainToEvenNiceTicks uses the nice-step ladder.
        if (Math.abs(getNiceStep(step) - step) > Math.max(step, 1) * 1e-8) continue;
        return Array.from({ length: intervals + 1 }, (_, i) => cleanStepValue(yMin + step * i, step));
    }
    const step = span / 5;
    return Array.from({ length: 6 }, (_, i) => Number((yMin + step * i).toFixed(6)));
};

/**
 * Y-axis [min, max] for bar charts: supports negative values, keeps 0 as an edge when all values
 * are on one side of zero, and pads beyond data extrema so LabelList text does not clip.
 * Final domain is snapped to evenly spaced nice ticks — use `getBarChartYTicks` on the YAxis.
 */
export const getBarChartYDomain = (
    minData: number,
    maxData: number,
    options?: { format?: 'percent' | 'currency' | 'number' | 'days'; capPercentMaxAt100?: boolean },
    plotHeightPx?: number,
): [number, number] => {
    const pad = BAR_LABEL_DOMAIN_PAD;

    if (minData === 0 && maxData === 0) {
        return options?.format === 'percent' ? [0, 5] : [0, 100];
    }

    let yMin = minData < 0 ? -getNiceDomainMax(-minData, pad) : 0;
    let yMax = maxData > 0 ? getNiceDomainMax(maxData, pad) : 0;

    if (yMin === yMax) {
        const bump = options?.format === 'percent' ? 1 : Math.max(Math.abs(yMin) * 0.15, 1);
        yMax = yMin + bump;
    }

    // Opt-in only: rates that cannot exceed 100% (e.g. auto submission). Margins can go above.
    if (options?.format === 'percent' && options.capPercentMaxAt100 === true) {
        yMax = Math.min(PERCENT_AXIS_MAX, yMax);
    }
  
    const [paddedMin, paddedMax] = applyLabelHeadroom(yMin, yMax, minData, maxData, options, plotHeightPx);
    const [snappedMin, snappedMax] = snapDomainToEvenNiceTicks(paddedMin, paddedMax);
    // Nice-tick snap can overshoot; keep opt-in capped rates at 100.
    if (options?.format === 'percent' && options.capPercentMaxAt100 === true) {
        return [snappedMin, Math.min(PERCENT_AXIS_MAX, snappedMax)];
    }
    return [snappedMin, snappedMax];
};

export const getPartTypeLabel = (name: string) => {
    const map: Record<string, string> = {
        'oem': 'OEM',
        'rcy': 'RCY',
        'recycled': 'RCY',
        'aftermarket': 'A/M',
        'a/m': 'A/M',
        'surplus': 'OE-Surplus',
        'oe_surplus': 'OE-Surplus',
        'oe-surplus': 'OE-Surplus',
        'oe surplus': 'OE-Surplus',
        'reman': 'Re-Man',
        're-man': 'Re-Man',
        're_man': 'Re-Man',
        'remanufactured': 'Re-Man',
        'recon': 'Re-Con',
        're-con': 'Re-Con',
        're_con': 'Re-Con',
        'other': 'Others',
        'others': 'Others'
    };
    // Try to match lowercase, fallback to original name
    const key = name?.toLowerCase();
    return map[key] || name;
};
